import { createFeature, createReducer, on } from '@ngrx/store';

import {
  AgentDto,
  AgentInputDto,
  AgentInstructionDto,
  AgentPromptDto,
  AgentVersionDto,
  AgentVersionHistoryDto,
} from './models/agent.model';
import { AgentsActions } from './agents.actions';

export interface AgentsState {
  error: string | null;
  loading: boolean;
  versionLoading: boolean;
  agentHubsLoading: boolean;
  agents: AgentDto[];
  selectedAgent: AgentDto | null;
  agentInstructions: AgentInstructionDto[];
  agentPrompts: AgentPromptDto[];
  agentInputs: AgentInputDto[];
  agentVersions: AgentVersionHistoryDto[];
  agentHubs: string[];
}

export const initialState: AgentsState = {
  error: null,
  loading: false,
  versionLoading: false,
  agentHubsLoading: false,
  agents: [],
  selectedAgent: null,
  agentInstructions: [],
  agentPrompts: [],
  agentInputs: [],
  agentVersions: [],
  agentHubs: [],
};

const reducer = createReducer(
  initialState,
  on(AgentsActions.getAgents, (state) => ({ ...state, loading: true })),
  on(AgentsActions.loadSuccess, (state, { agents }) => ({
    ...state,
    agents: agents?.map((a) => ({ ...a })) ?? [],
    loading: false,
  })),
  on(AgentsActions.loadFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.getAgentDetail, (state) => ({ ...state, selectedAgent: null, loading: true })),
  on(AgentsActions.getAgentSuccess, (state, { agent }) => ({
    ...state,
    selectedAgent: agent,
    loading: false,
  })),
  on(AgentsActions.getAgentFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    selectedAgent: null,
    loading: false,
  })),
  on(AgentsActions.getInstructionDetail, (state) => ({
    ...state,
    agentInstructions: [],
    loading: true,
  })),
  on(AgentsActions.getInstructionSuccess, (state, { agentInstructions }) => ({
    ...state,
    agentInstructions: [...agentInstructions].sort((a, b) => a.displayOrder - b.displayOrder),
    loading: false,
  })),
  on(AgentsActions.getInstructionFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    agentInstructions: [],
    loading: false,
  })),
  on(AgentsActions.getPrompts, (state) => ({ ...state, agentPrompts: [], loading: true })),
  on(AgentsActions.getPromptsSuccess, (state, { agentPrompts }) => ({
    ...state,
    agentPrompts: agentPrompts?.map((a) => ({ ...a })) ?? [],
    loading: false,
  })),
  on(AgentsActions.getPromptsFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    agentPrompts: [],
    loading: false,
  })),
  on(AgentsActions.updatePrompt, (state) => ({ ...state, loading: true })),
  on(AgentsActions.updatePromptSuccess, (state, { agentPrompt }) => {
    return {
      ...state,
      agentPrompts: state.agentPrompts.map((ap) => {
        if (ap.agentPromptId === agentPrompt.agentPromptId) {
          return { ...agentPrompt };
        }
        return { ...ap };
      }),
      loading: false,
    };
  }),
  on(AgentsActions.updatePromptFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.createPrompt, (state) => ({ ...state, loading: true })),
  on(AgentsActions.createPromptSuccess, (state, { agentPrompt }) => {
    return {
      ...state,
      agentPrompts: [...state.agentPrompts, agentPrompt],
      loading: false,
    };
  }),
  on(AgentsActions.createPromptFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.deletePrompt, (state) => ({ ...state, loading: true })),
  on(AgentsActions.deletePromptSuccess, (state, { agentPromptId }) => {
    return {
      ...state,
      agentPrompts: [...state.agentPrompts.filter((ap) => ap.agentPromptId !== agentPromptId)],
      loading: false,
    };
  }),
  on(AgentsActions.deletePromptFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.updateAgent, (state) => ({ ...state, loading: true })),
  on(AgentsActions.updateAgentSuccess, (state, { agent }) => {
    return {
      ...state,
      agents: state.agents.map((a) => {
        if (a.agentUuid === agent.agentUuid) {
          return { ...agent };
        }
        return { ...a };
      }),
      selectedAgent: agent,
      loading: false,
    };
  }),
  on(AgentsActions.updateAgentFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.createAgent, (state) => ({ ...state, loading: true })),
  on(AgentsActions.createAgentSuccess, (state, { agent }) => {
    return {
      ...state,
      agents: [...state.agents, agent],
      selectedAgent: agent,
      loading: false,
    };
  }),
  on(AgentsActions.createAgentFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.updateInstruction, (state) => ({ ...state, loading: true })),
  on(AgentsActions.updateInstructionFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.createInstruction, (state) => ({ ...state, loading: true })),
  on(AgentsActions.createInstructionFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.deleteInstruction, (state) => ({ ...state, loading: true })),
  on(AgentsActions.deleteInstructionFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(AgentsActions.deleteAgent, (state) => ({ ...state, loading: true })),
  on(AgentsActions.deleteAgentSuccess, (state, { agentUuid }) => {
    return {
      ...state,
      agents: state.agents.filter((agent) => agent.agentUuid !== agentUuid),
      loading: false,
    };
  }),
  on(AgentsActions.deleteAgentFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),

  on(AgentsActions.getAgentVersions, (state) => ({ ...state, versionLoading: true })),
  on(AgentsActions.getAgentVersionsSuccess, (state, { agentVersions }) => ({
    ...state,
    agentVersions: agentVersions?.map((a) => ({ ...a })) ?? [],
    versionLoading: false,
  })),
  on(AgentsActions.getAgentVersionsFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    agentVersions: [],
    versionLoading: false,
  })),
  on(AgentsActions.getAgentHubs, (state) => ({ ...state, agentHubsLoading: true })),
  on(AgentsActions.getAgentHubsSuccess, (state, { agentHubs }) => ({
    ...state,
    agentHubs: [...agentHubs],
    agentHubsLoading: false,
  })),
  on(AgentsActions.getAgentHubsFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    agentHubsLoading: false,
  })),
  on(AgentsActions.clearSelectedAgentState, (state) => ({
    ...state,
    selectedAgent: null,
    agentInstructions: [],
    agentPrompts: [],
    agentVersions: [],
    agentInputs: [],
  })),
  on(AgentsActions.getAgentInputs, (state) => ({ ...state, agentInputs: [], loading: true })),
  on(AgentsActions.getAgentInputsSuccess, (state, { agentInputs }) => ({
    ...state,
    agentInputs: agentInputs?.map((a) => ({ ...a })) ?? [],
    loading: false,
  })),
  on(AgentsActions.getAgentInputsFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    agentInputs: [],
    loading: false,
  })),
);

export const agentsFeature = createFeature({
  name: 'agents',
  reducer,
});
